import React, { lazy, Suspense } from "react";
import ShiftManagementdesktop from "@/assets/shift-management/waggex-shift-management-software-dashboard-india.webp?w=1200&format=webp";
import ShiftManagementMobile from "@/assets/shift-management/waggex-shift-management-software-dashboard-india-mobile.webp?w=350&format=webp";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("./HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
const DynamicFeaturesSection = lazy(
  () => import("../components/common/DynamicFeaturesSection"),
);
import {
  Bell,
  AlertTriangle,
  UserX,
  Map,
  Check,
  IndianRupee,
  RefreshCw,
} from "lucide-react";

import StatsSection from "../components/common/StatsSection";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";

const shiftProblems = [
  {
    icon: RefreshCw,
    title: "Manual Shift Planning",
    desc: "No clear process to define and assign shifts at scale.",
  },
  {
    icon: RefreshCw,
    title: "Rotational Shift Confusion",
    desc: "Rotational schedules built in spreadsheets break and create scheduling errors.",
  },
  {
    icon: RefreshCw,
    title: "Overtime Mismanagement",
    desc: "No system tracking overtime hours before payroll processing.",
  },
  {
    icon: RefreshCw,
    title: "Payroll Errors",
    desc: "Shift timing gaps cause wrong working-day and salary calculations.",
  },
];
const shiftMangFeatures = [
  // ROW 1
  {
    id: 1,
    title: "Create Shift Timings",
    desc: "Define precise shift start and end times, break durations, role, or location in your organisation.",
  },
  {
    id: 2,
    title: "Employee Shift Assignment",
    desc: "Assign employees or teams to shifts instantly with automatic attendance and payroll synchronization.",
  },
  {
    id: 3,
    title: "Rotational Shift Planning",
    desc: "Create automatic weekly or monthly rotational shifts without manual rescheduling or repetitive planning.",
  },

  // ROW 2
  {
    id: 4,
    title: "Field Team Shift Setup",
    desc: "Manage shifts for field, remote, and multi-location teams with GPS-based attendance and location-specific rules.",
  },
  {
    id: 5,
    title: "Shift-Based Attendance",
    desc: "Track late entries, early exits, absences, and attendance records linked automatically with every employee shift.",
  },
  {
    id: 6,
    title: "Real-Time Review",
    desc: "Monitor shift coverage, attendance gaps, overtime alerts, and unassigned shifts from one live dashboard.",
  },

  // ROW 3
  {
    id: 7,
    title: "Payroll-Ready Shift Data",
    desc: "Employee shift hours, overtime, and short hours sync directly with payroll without manual calculations or reconciliation.",
  },
  {
    id: 8,
    title: "Shift Reports",
    desc: "Generate shift utilization, overtime, and employee shift reports instantly for any selected date range.",
  },
  {
    id: 9,
    title: "Admin Control",
    desc: "Approve, edit, and override employee shifts with complete audit tracking across all departments.",
  },
];

const shifts = [
  {
    title: "Day Shift",
    desc: "Standard daytime shifts with fixed hours",
    items: [
      "Fixed morning-to-evening timing",
      "Ideal for office and retail teams",
      "Attendance auto-tracked per shift",
    ],
    icon: "☀️",
  },
  {
    title: "Night Shift",
    desc: "Overnight shifts with adjusted rules",
    items: [
      "Overnight hours tracked accurately",
      "Separate overtime calculation",
      "Night shift allowances supported",
    ],
    icon: "🌙",
  },
  {
    title: "Rotational Shift",
    desc: "Automated rotation across shift cycles",
    items: [
      "Weekly or monthly rotation cycles",
      "Assign multiple shift patterns",
      "Reduce planning hours drastically",
    ],
    icon: "🔄",
  },
  {
    title: "Flexible Shift",
    desc: "Flexible work windows for hybrid teams",
    items: [
      "Flexible start and end windows",
      "Outcome-based attendance marking",
      "Ideal for remote and hybrid teams",
    ],
    icon: "⏱️",
  },
];
const headers = [
  "SHIFT ACTIVITY",
  "MANUAL SHIFT PLANNING",
  "WAGGEX SHIFT MANAGEMENT",
];

const rows = [
  {
    feature: "Shift Creation",
    without: "Excel sheets or manual notes",
    with: "Digital shift creation system",
  },
  {
    feature: "Employee Assignment",
    without: "Manual communication (calls/messages)",
    with: "Employee-wise automated shift assignment",
  },
  {
    feature: "Rotational Shifts",
    without: "Difficult to manage manually",
    with: "Systematic rotation planning with automation",
  },
  {
    feature: "Attendance Connection",
    without: "Separate attendance records",
    with: "Shift-based attendance tracking system",
  },
  {
    feature: "Overtime Review",
    without: "Manual calculation and checking",
    with: "Automated overtime-linked shift tracking",
  },
  {
    feature: "Shift History",
    without: "Hard to find past shift data",
    with: "Employee-wise searchable shift records",
  },
  {
    feature: "Payroll Support",
    without: "Manual salary checking",
    with: "Payroll-ready shift data automatically synced",
  },
];
const shiftMangFaqs1 = [
  {
    title: "Is My Data Secure With The Waggex Payroll Web App?",
    content:
      "Yes. The Waggex App enforces Grade SSL Encryption, Multi-Factor Authentication, and Encrypted Backups to protect your business data. All shift records, employee schedules, and attendance information are stored securely with role-based access so only authorised users can view or make changes.",
  },
  {
    title: "Can I Create More Than One Company On Waggex?",
    content:
      "Yes. Waggex supports multiple company accounts under a single login. Each company operates independently with its own employees, shift structures, attendance policies, and payroll settings. You can switch between accounts from the same dashboard without logging out.",
  },
  {
    title: "Does Waggex support rotational shift scheduling?",
    content:
      "Yes. Waggex supports full rotational shift scheduling. HR teams define the rotation pattern once weekly, fortnightly, or monthly and the system applies it automatically across assigned employees. Every rotation is visible to both HR and employees through the Staff App and HR dashboard.",
  },
];

const shiftMangFaqs2 = [
  {
    title: "Can shift records connect with payroll processing?",
    content:
      "Yes. Every shift record in Waggex flows directly into payroll. Working hours, overtime, short-hour deductions, and shift-based allowances are applied automatically at the point of salary processing with no manual cross-checking between the shift schedule and payroll system.",
  },
  {
    title: "Is My Data Secure With The Waggex Payroll Web App?",
    content:
      "Yes. The Waggex App enforces Grade SSL Encryption, Multi-Factor Authentication, and Encrypted Backups. All shift records, employee assignments, and attendance data are stored securely with role-based access controls across every user.",
  },
];
const features = [
  "Create multiple shifts for different departments and teams",
  "Assign staff timing with attendance and overtime tracking",
  "Connect shift timing with attendance and overtime system",
  "Link shift records before payroll and payslip generation",
];
const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Shift Management Software",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    description:
      "Waggex Shift Management Software automates employee shift creation, assignment, rotational planning, attendance connection, overtime tracking, and payroll-ready shift data for Indian businesses.",
    url: "https://www.waggex.com/shift-management",
    featureList: [
      "Employee shift scheduling software India",
      "Shift schedule app India",
      "Shift scheduling application",
      "Rotational shift management India",
      "Field team shift planning",
      "Shift-based attendance tracking",
      "Payroll-ready shift data",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "312",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Waggex Shift Management Software?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex Shift Management Software is an employee shift scheduling application that automates shift creation, employee assignment, rotational planning, attendance tracking, and payroll-ready shift data for Indian businesses of all sizes.",
        },
      },
      {
        "@type": "Question",
        name: "Does Waggex support rotational shift scheduling?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex supports rotational shift cycles. HR defines the pattern once, and the system automatically applies it across employees on a recurring basis.",
        },
      },
      {
        "@type": "Question",
        name: "Can shift records connect with payroll processing?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Shift data flows directly into payroll, including working hours, overtime, deductions, and allowances without manual intervention.",
        },
      },
      {
        "@type": "Question",
        name: "Is My Data Secure With The Waggex Payroll Web App?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex uses SSL encryption, multi-factor authentication, encrypted backups, and role-based access controls to secure all employee and shift data.",
        },
      },
      {
        "@type": "Question",
        name: "Can Waggex manage shifts for multiple business locations?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Multiple locations can be managed under one account with centralized reporting and location-specific shift rules.",
        },
      },
      {
        "@type": "Question",
        name: "Can I Create More Than One Company On Waggex?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Multiple company accounts operate independently under one login with separate shift structures, employees, and payroll settings.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides shift management software and complete HRMS solutions for Indian businesses.",
    areaServed: "IN",
  },
];
const cards = [
  {
    title: "Manual Attendance",
    items: [
      "Useful for corrections",
      "Admin-controlled entries",
      "Simple attendance review",
    ],
  },
  {
    title: "Selfie Attendance",
    items: [
      "Employee-friendly check-in",
      "Photo-based record support",
      "Useful for remote teams",
    ],
  },
  {
    title: "Geo-Location Attendance",
    items: [
      "Location-supported records",
      "Useful for field staff",
      "Workplace check-in control",
    ],
  },
  {
    title: "FaceLens AI Attendance",
    items: [
      "Location-supported records",
      "Useful for field staff",
      "Helps reduce proxy attendance",
    ],
  },
];

const appfeatures = [
  {
    icon: <AlertTriangle size={24} aria-hidden="true" />,
    title: "Manual Entry Errors",
    desc: "Small attendance mistakes in registers or Excel can lead to major payroll and reporting issues.",
  },
  {
    icon: <UserX size={24} aria-hidden="true" />,
    title: "Proxy Attendance",
    desc: "Traditional attendance systems make it easy for fake or inaccurate check-ins without verification.",
  },
  {
    icon: <Map size={24} aria-hidden="true" />,
    title: "Field Staff Confusion",
    desc: "Difficult to track and verify attendance of employees working across multiple locations.",
  },
  {
    icon: <IndianRupee size={24} aria-hidden="true" />,
    title: "Payroll Mismatch",
    desc: "Incorrect attendance data leads to salary errors, overtime disputes, and delayed payroll processing.",
  },
];

export default function ShiftManagement() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>
          Free Shift Management & Staff Scheduling Software | Waggex
        </title>
        <meta
          name="description"
          content="Manage employee shifts, rotational schedules, attendance, and overtime with smart shift management software by Waggex"
        />
        <meta
          name="keywords"
          content="shift management software,employee shift scheduling software,shift schedule app,shift scheduling application,shift management system India,online shift planner India,employee shift roster software,work shift management India,HR shift management software India"
        />
        <link rel="canonical" href="https://www.waggex.com/shift-management" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d3e0eb]">
        <div className="mx-auto px-4">
          {/* TOP CONTENT */}
          <div className="text-center top-10 relative">
            <h1 className="text-xl sm:text-3xl font-roboto font-bold text-[#10324F] leading-tight min-h-13">
              Online Shift Management Software for Employee Scheduling
            </h1>

            <p className="mt-2 text-sm sm:text-base lg:text-lg text-[#10324F] max-w-3xl mx-auto">
              TWaggex shift management software gives HR teams and operations
              managers complete control over employee shift schedules. Define
              shift timings, assign employees, manage rotations, and let every
              shift record feed automatically into attendance and payroll
            </p>

            {/* BUTTONS */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-[#10324F] text-white font-semibold"
              >
                Start Free Today
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto px-8 py-3 rounded-xl border border-[#10324F] text-[#10324F]"
              >
                Book Free Demo
              </button>
            </div>
          </div>

          {/* BOTTOM IMAGE SECTION */}
          <div className="mt-20 flex justify-center">
            <div className="relative mb-10">
              <picture>
                <source
                  media="(max-width: 639px)"
                  srcSet={ShiftManagementMobile}
                />
                <img
                  src={ShiftManagementdesktop}
                  width={1303}
                  height={262}
                  fetchPriority="high"
                  alt="Waggex shift management software dashboard showing shift creation employee assignment rotational planning and payroll-ready shift data India"
                  className="w-full h-auto object-contain"
                />
              </picture>
              {/* <img
                src={ShiftManagementMobile}
                width={380}
                height={243}
                fetchPriority="high"
                alt="Waggex shift management software dashboard showing shift creation employee assignment rotational planning and payroll-ready shift data India"
                className="w-full h-auto object-contain block sm:hidden"
              /> */}
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="bg-[#FFFFFF] min-h-130 py-14 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-xl sm:text-3xl font-bold text-[#18314F] leading-tight  mx-auto min-h-15">
            End Excel-Based Shift Scheduling
          </h2>

          <p className="mt-5 text-sm sm:text-base  text-[#10324F] max-w-4xl mx-auto leading-relaxed mb-10">
            Manual shift planning creates confusion for HR teams, managers, and
            employees. Wrong shift timings, unchecked overtime, missing working
            hours, and late attendance records can directly affect salary
            calculations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {shiftProblems.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="flex gap-4 p-4 rounded-xl border border-white"
                  style={{
                    boxShadow: `
                  rgb(167 191 210 / 40%) -15px -15px 20px inset,
                  rgb(170 188 202 / 30%) -20px -20px 30px
                `,
                  }}
                >
                  {/* ICON */}
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-[#10324F] text-white">
                    <Icon size={28} aria-hidden="true" />
                  </div>

                  {/* CONTENT */}
                  <div>
                    <h2 className="font-roboto text-[#10324F] font-semibold text-sm md:text-base text-left">
                      {item.title}
                    </h2>
                    <p className="font-roboto text-[#10324F] text-sm mt-1 text-left max-w-xs">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
            Easily Plan, Assign & Manage Employee Shifts
          </h2>
          <p className="font-roboto text-[#10324F] text-center text-sm md:text-base font-medium leading-relaxed py-4 max-w-3xl mx-auto mb-4">
            Waggex gives businesses a centralized shift scheduling application
            where shift timings, employee assignments, attendance records, and
            payroll-ready shift data are always connected and current.
          </p>
          {/* Features Grid */}
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-12 place-items-start">
            {features.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 text-[#10324F] px-6 py-6 bg-white
            rounded-2xl"
                style={{
                  boxShadow: `
              rgb(167 191 210 / 35%) -12px -12px 20px inset,
              rgb(170 188 202 / 22%) -14px -14px 24px
            `,
                }}
              >
                {/* Diamond Icon */}
                <div className=" mt-1 flex items-center justify-center text-[#10324F]">
                  <Check size={30} aria-hidden="true" />
                </div>
                {/* Text */}
                <p className="font-roboto text-sm md:text-base font-medium leading-relaxed">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <DynamicFeaturesSection
          heading="All-in-One Shift Management Features"
          description="Waggex Shift Management helps HR teams manage shift creation, employee assignment, attendance tracking, overtime reports, and payroll-ready shift records"
          dynamicFeatures={shiftMangFeatures}
        />
        <section className="w-full bg-[#D1DFEA33] py-10 px-6">
          <div className="max-w-6xl mx-auto">
            {/* Heading */}
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-3xl font-bold text-[#10324F]">
                Manage Different Shifts as Per Your Business Needs
              </h2>
              <p className="mt-4 text-[#10324F] max-w-3xl mx-auto">
                Waggex helps you organize different shift types for office
                teams, field employees, support teams, factories, service
                businesses, and growing organizations.
              </p>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {shifts.map((shift, index) => (
                <div
                  key={index}
                  className=" rounded-2xl shadow-md hover:shadow-xl transition duration-300 p-6 border border-white"
                  style={{
                    boxShadow: `
                  rgb(167 191 210 / 40%) -15px -15px 20px inset,
                  rgb(170 188 202 / 30%) -20px -20px 30px
                `,
                  }}
                >
                  {/* Icon + Title */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{shift.icon}</span>
                    <h2 className="font-roboto text-lg font-semibold text-[#10324F]">
                      {shift.title}
                    </h2>
                  </div>

                  {/* Description */}
                  <p className="font-roboto text-sm text-[#10324F] mb-4 leading-tight max-w-md h-8">
                    {shift.desc}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {shift.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="font-roboto text-[#10324F] mt-1">
                          ▶
                        </span>
                        <span className="font-roboto text-[#10324F]">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ComparisonTable
          headers={headers}
          rows={rows}
          title="Waggex Shift Management vs Manual Shift Planning"
          subtitle="More than visual schedules updates to a structured shift management workflow"
          source="payrollfeature"
        />
        <CustomerReviews />
        {/* <FAQ /> */}
        <HomeFaq
          faq1={shiftMangFaqs1}
          faq2={shiftMangFaqs2}
          subtitle="Common questions from Indian businesses evaluating Waggex Shift Management Software"
        />
        <PayrollBanner
          heading="JUST NEED PAYROLL SOFTWARE?"
          description="Get dedicated payroll software built for accurate salary processing, compliance, payslips, and payroll automation"
          buttonText="View Payroll Software"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading="Simplify Payroll Processing With Waggex"
          description="Run accurate, compliant payroll every month using payroll management software built for the way Indian businesses actually operate. No spreadsheets. No errors. Start free today."
        />
      </Suspense>
    </>
  );
}
